// email.service.ts
import { inject, Injectable } from '@angular/core';
import { SupabaseAuthService } from './supabase-auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root' 
})
export class EdgeFunctionCallerService {

  private supabaseAuthService = inject(SupabaseAuthService);

  async sendNotification(payload: {
    to: string;
    template: string;
    language: string;
    userName: string;
    psychologistName: string;
    adminNote?: string;
  }) {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    if (!accessToken) throw new Error('Not authenticated');
    const res = await fetch('/functions/v1/send-profile-notification', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to send');
    return data;
  }

  async updateProfileToPsychologist(userId: string){

    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;

    const res = await fetch('/functions/v1/update-user-profile', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId
      })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed update profile');
    return data;

  }

  async startSubscription(userEmail:string, userName: string, priceId:string) {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    const response = await fetch('/functions/v1/create-checkout-session', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId: priceId,
        customerEmail: userEmail,
        customerName: userName
      }),
    });

    const { url } = await response.json();
    if (url) window.location.replace(url);
  }

  async startVIPSubscription(userEmail:string, userName: string, priceId:string) {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    const res = await fetch('/functions/v1/create-vip-subscription', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId: priceId,
        customerEmail: userEmail,
        customerName: userName
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed creating VIP subscription');
    return data;
    
  }

  async manageSubscription() {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;

    const response = await fetch('/functions/v1/create-subscription-portal-session', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    const { url } = await response.json();
    if (url) window.location.replace(url);
  }

  async reconcileSubscription(userEmail: string, userId: string) {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    if (!accessToken) throw new Error('Not authenticated');

    const res = await fetch('/functions/v1/reconcile-stripe-subscription', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: userEmail,
        userId: userId
      })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to send');
    return data;
  }

  async linkGoogleCalendar(userId: string) {
    window.location.href =
      `${environment.SUPABASE_URL}/functions/v1/google-oauth-start?state=${userId}`;
  }

  async unlinkGoogleCalendar() {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    if (!accessToken) throw new Error('Not authenticated');

    const res = await fetch('/functions/v1/google-oauth-revoke', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to send');
    return data;
  }

  async googleCalendarEvent(payload: {
    action: "create" | "update" | "delete",
    userId : string,
    title: string,
    startDateTime: string,
    endDateTime: string,
    timeZone: string,
    eventId: string | null | undefined,
    patientEmail: string
  }) {
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    if (!accessToken) throw new Error('Not authenticated');

    const res = await fetch('/functions/v1/google-calendar-event', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to send');
    return data;
  }

  async addPushNotification(
    userId: string, 
    scheduledFor: string,
    title: string,
    body: string,
    data: any
  ){
    
    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    if (!accessToken) throw new Error('Not authenticated');

    const payload = {
      userId: userId,
      scheduledFor: scheduledFor,
      title: title,
      body: body,
      data: data
    };

    const res = await fetch('/functions/v1/add-notification', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const dataReturned = await res.json();
    if (!res.ok) throw new Error(dataReturned?.error || 'Failed to send');
    return dataReturned;


  }

}

